import { ReactNode, useCallback, useEffect, useState } from 'react';

import { Button, Popover, Tooltip, XmarkIcon } from '../../index';
import { addStyles } from '../../utils/helpers';

export interface FilterChipProps {
  /** The label of the FilterChip */
  value: string;
  /** The first selection to be displayed next to the label */
  firstSelection?: string;
  /** A number representing the count of selected items. This displays "and {count - 1} more" */
  numberSelected: number;
  /** Whether the menu is open */
  isOpen: boolean;
  /** Callback to be called when the menu is opened */
  onOpen?: () => void;
  /** DEPRECATED; The width of the FilterChip */
  width?: string;
  /** The content to display in the Dropdown */
  menuContent: ReactNode;
  /** Callback to be called when the selection is cleared
   * This should be used to clear the selection in the parent component
   */
  onClearSelection: () => void;
  /** The icon to display next to the label */
  icon?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  >;
}

const options = {
  container: {
    base: 'flex flex-nowrap items-center box-border rounded-lg border-solid h-min cursor-pointer pl-3',
    unselected: 'border-gray-600 bg-white text-gray-900 border',
    selected: 'border-midnight-900 bg-white border-2',
  },
  text: {
    base: 'text-sm select-none font-medium truncate pr-1',
    selected: '',
    unselected: '',
  },
  selectionText: {
    base: 'text-sm select-none truncate pr-1',
    selected: '',
    unselected: '',
  },
  icon: {
    base: 'h-6 w-6',
    selected: '',
    unselected: '',
  },
};

const getStyleKey = (isSelected: boolean): 'selected' | 'unselected' => {
  return isSelected ? 'selected' : 'unselected';
};

// eslint-disable-next-line max-lines-per-function
export const FilterChip = (props: FilterChipProps) => {
  const {
    value,
    numberSelected,
    firstSelection = '',
    isOpen: defaultIsOpen,
    onClearSelection,
    width = '352px',
    menuContent,
    icon,
    onOpen,
  } = props;
  const [menuOpen, setMenuOpen] = useState<boolean>(defaultIsOpen);
  // openMenu function guards against calling onOpen when the menu is already open
  const openMenu = () => {
    if (!menuOpen) {
      setMenuOpen(true);
      onOpen?.();
    }
  };

  const isSelected = numberSelected > 0;

  useEffect(() => {
    if (defaultIsOpen) {
      openMenu();
    } else {
      setMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultIsOpen]);

  const onOpenChange = useCallback((isOpen: boolean) => setMenuOpen(isOpen), [setMenuOpen]);

  const styleKey = getStyleKey(menuOpen);
  const containerStyle = options.container.base + ' ' + options.container[styleKey];

  const selectionText = numberSelected > 1 ? `${firstSelection} and ${numberSelected - 1} more` : firstSelection;

  return (
    <Popover
      open={menuOpen}
      trigger="click"
      innerContentClassName=""
      showArrow={false}
      content={menuContent}
      onOpenChange={onOpenChange}
      autoPlacementOptions={{ allowedPlacements: ['top', 'bottom'] }}
    >
      <div
        onClick={(e) => {
          e.stopPropagation();
          openMenu();
        }}
        className={containerStyle}
        style={{ width: width, maxWidth: width }}
      >
        {icon && (
          <div className="pr-2">
            {icon({
              className: addStyles([options.icon.base, options.icon[styleKey]]),
            })}
          </div>
        )}
        <span className={addStyles([options.text.base, options.text[styleKey]])}>
          <Tooltip
            title={value}
            innerContentClassName="whitespace-nowrap"
            overlayStyle={{ maxWidth: 'none', minWidth: 'fit-content', width: 'auto' }}
          >
            {/* div is needed for tooltip to work*/}
            <div className="truncate">{value}</div>
          </Tooltip>
        </span>
        {isSelected && (
          <span className={addStyles([options.selectionText.base, options.selectionText[styleKey]])}>
            {selectionText}
          </span>
        )}
        <div className="ml-auto">
          <Button
            icon={XmarkIcon}
            title="Clear selection"
            onClick={(e) => {
              e.stopPropagation();
              onClearSelection();
              setMenuOpen(false);
            }}
            type="text"
            size="small"
          />
        </div>
      </div>
    </Popover>
  );
};

export default FilterChip;
